import { Injectable } from '@angular/core';
import { AnalyticsService } from './analytics.service';
import { GA4_EVENTS, VENDOR_EVENTS } from './analytics.events';
import { EventProperties } from './analytics.types';

/**
 * Vendor onboarding funnel — profile → bio → samples → AI review → rates → live.
 *
 * THE PROBLEM THIS SOLVES, AND WHY IT ISN'T A ONE-LINER
 * ────────────────────────────────────────────────────────────────────────
 * The vendor's progress is SERVER state, read back on every page load via
 * `GET /vendor/onboard/{id}`. So the naive instrumentation — "fire an event
 * when we see bio_submitted" — re-fires `vendor_bio_updated` every single time
 * the vendor opens any page, forever. The funnel would show thousands of bio
 * completions from a handful of vendors and every conversion rate built on it
 * would be nonsense.
 *
 * So this keeps a HIGH-WATER MARK per vendor: the furthest stage already
 * reported. An event fires only for stages newly crossed since then, and the
 * mark is persisted so it survives reloads.
 *
 * WHY localStorage AND NOT MEMORY
 * ────────────────────────────────────────────────────────────────────────
 * A vendor uploads samples, the page navigates, the service is re-created and
 * in-memory state is gone — so the next status fetch would look like a fresh
 * crossing. The mark has to outlive the page, and localStorage is the only
 * per-device store available here. It is keyed per vendor so two vendors on a
 * shared machine cannot suppress each other's events.
 *
 * WHAT THIS COSTS, STATED HONESTLY
 * ────────────────────────────────────────────────────────────────────────
 * A vendor who clears site data or switches device can re-fire stages they had
 * already crossed. That is a known, bounded duplicate — and it is far better
 * than the alternative, which re-fires on every page view. Mixpanel can
 * de-duplicate on (vendor, stage) at report time; it cannot invent a signal
 * that was drowned in noise.
 *
 * The genuinely correct fix is for the backend to emit these once, at the
 * moment the state changes. That is Phase 4 work, in `studio-ip backend`.
 * Until then, this is the honest approximation.
 */
@Injectable({ providedIn: 'root' })
export class VendorOnboardingTracker {
  /** Funnel order. Index = how far along the vendor is. */
  private static readonly STAGES = [
    'pending',
    'bio_done',
    'samples_done',
    'comparison_done',
    'rate_done',
    'completed',
  ] as const;

  /** Stage → the event announcing that it was reached. */
  private static readonly STAGE_EVENT: Record<string, string> = {
    bio_done: VENDOR_EVENTS.VENDOR_BIO_UPDATED,
    samples_done: VENDOR_EVENTS.VENDOR_PORTFOLIO_UPLOADED,
    rate_done: VENDOR_EVENTS.VENDOR_RATES_UPDATED,
    completed: VENDOR_EVENTS.VENDOR_PROFILE_COMPLETED,
  };

  private static readonly KEY_PREFIX = 'sip_onb_stage_';

  constructor(private analytics: AnalyticsService) {}

  /**
   * Called with whatever `GET /vendor/onboard/{id}` returned.
   *
   * Derives the stage using the SAME rules as the dashboard and the stepper —
   * see `vc-onboard-stepper.derive()`. Deliberately duplicated rather than
   * imported: those two already mirror each other by hand, and importing one
   * component's private logic into an analytics service would couple the
   * funnel to a UI file. If the rules change, all three change together.
   */
  statusObserved(vendorId: string | number | null | undefined, status: any): void {
    try {
      if (!vendorId || !status) return;
      const stage = this.deriveStage(status);
      const reached = VendorOnboardingTracker.STAGES.indexOf(stage as any);
      if (reached <= 0) return;   // 'pending' announces nothing

      const key = VendorOnboardingTracker.KEY_PREFIX + String(vendorId);
      const previous = this.readMark(key);
      if (reached <= previous) return;   // nothing new — the common case

      const props = this.statusProperties(status, vendorId);

      // Fire every stage crossed since last time, in order. A vendor who
      // uploads samples and sets rates before the next status fetch has
      // genuinely passed both, and skipping the intermediate one would leave
      // a hole in the funnel that looks like a drop-off.
      for (let i = previous + 1; i <= reached; i++) {
        const name = VendorOnboardingTracker.STAGES[i];
        const event = VendorOnboardingTracker.STAGE_EVENT[name];
        if (event) {
          this.analytics.track(event as any, { ...props, stage: name });
        }
        if (name === 'comparison_done') {
          // The AI readiness verdict, not a vendor action. Which of the two
          // fires is the single most important signal in vendor onboarding:
          // it decides whether this vendor can be invited to anything at all.
          const ready = status?.readiness_status === 'bid_ready';
          this.analytics.track(
            (ready ? VENDOR_EVENTS.VENDOR_AI_BID_READY
                   : VENDOR_EVENTS.VENDOR_AI_NOT_BID_READY) as any,
            { ...props, readiness_status: status?.readiness_status ?? null },
          );
        }
        if (name === 'completed') {
          this.analytics.track(VENDOR_EVENTS.VENDOR_ONBOARDED as any, props);
          // GA4's marketing-facing equivalent — this is the vendor-acquisition
          // conversion campaigns are optimised against.
          this.analytics.track(GA4_EVENTS.VENDOR_PROFILE_COMPLETE as any, {
            service_category: props['service_category'],
            city: props['city'],
          });
        }
      }

      this.writeMark(key, reached);

      // Keep the People profile in step, so vendors can be segmented by how
      // far they got without replaying their event history.
      this.analytics.setUserProperties({
        onboarding_stage: stage,
        bio_done: reached >= 1,
        samples_done: reached >= 2,
        rate_done: reached >= 4,
        ai_ready: status?.readiness_status === 'bid_ready',
        samples_count: Number(status?.samples_count || 0),
      });
    } catch { /* never break onboarding */ }
  }

  /** The vendor opened the profile/onboarding flow for the first time. */
  profileStarted(vendorId: string | number | null | undefined): void {
    try {
      if (!vendorId) return;
      const key = VendorOnboardingTracker.KEY_PREFIX + 'started_' + String(vendorId);
      if (this.readRaw(key)) return;     // once per vendor per device
      this.writeRaw(key, '1');
      this.analytics.track(VENDOR_EVENTS.VENDOR_PROFILE_STARTED, { vendor_id: String(vendorId) });
    } catch { /* never break onboarding */ }
  }

  // ── Internals ──────────────────────────────────────────────────────────

  /** Mirrors vc-onboard-stepper.derive() and vc-dashboard's copy of it. */
  private deriveStage(res: any): string {
    const bio = !!res?.bio_submitted;
    const samplesSubmitted = !!res?.samples_submitted;
    const samplesCount = Number(res?.samples_count || 0);
    const comparison =
      res?.comparison_analysis &&
      typeof res.comparison_analysis === 'object' &&
      Object.keys(res.comparison_analysis).length > 0;
    const rate = !!res?.rate_submitted;
    // 'bid_ready' alone is not live — the dashboard guards against a stray
    // bid_ready with rate_submitted false, and this must agree with it.
    const live = res?.readiness_status === 'bid_ready' && rate;

    if (live) return 'completed';
    if (rate) return 'rate_done';
    if (comparison) return 'comparison_done';
    if (samplesSubmitted && samplesCount > 0) return 'samples_done';
    if (bio) return 'bio_done';
    return 'pending';
  }

  /** The doc's vendor properties: Services, City, Experience, Portfolio Count. */
  private statusProperties(status: any, vendorId: string | number): EventProperties {
    const services: string[] =
      status?.services || status?.bid_ready_services || status?.claimed_services || [];
    return {
      vendor_id: String(vendorId),
      service_category: services.length ? services[0] : null,
      services: services.length ? services : null,
      city: status?.city || status?.location || null,
      experience_band: status?.experience_band || null,
      portfolio_count: Number(status?.samples_count || 0),
      readiness_status: status?.readiness_status || null,
      rate_submitted: !!status?.rate_submitted,
    };
  }

  private readMark(key: string): number {
    const raw = this.readRaw(key);
    const n = raw == null ? -1 : Number(raw);
    return Number.isFinite(n) ? n : -1;
  }

  private writeMark(key: string, value: number): void {
    this.writeRaw(key, String(value));
  }

  /** localStorage can throw (Safari private mode, storage disabled). A vendor
   *  must never fail to onboard because analytics could not write a marker. */
  private readRaw(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
  }

  private writeRaw(key: string, value: string): void {
    try { localStorage.setItem(key, value); } catch { /* ignore */ }
  }
}
